import { useQuery } from '@tanstack/react-query';
import { CodeEditor } from '@kodprep/platform-ui/organisms';
import { cn } from '@/lib/cn';
import { useEditorStore } from '@/stores/editorStore';
import { submissionsApi } from '@/lib/api/submissions';
import { queryKeys } from '@/lib/queryKeys';

// Human-readable verdict labels (raw enum -> LeetCode-style label).
export const VERDICT_LABEL: Record<string, string> = {
  AC: 'Accepted',
  WA: 'Wrong Answer',
  TLE: 'Time Limit Exceeded',
  MLE: 'Memory Limit Exceeded',
  RE: 'Runtime Error',
  CE: 'Compilation Error',
  PENDING: 'Pending',
};

export const verdictColorClass = (verdict: string) =>
  verdict === 'AC' ? 'text-green-600 dark:text-green-400' : 'text-red-500';

// RuntimeChart plots the distribution of accepted runtimes for the problem and
// marks this submission's bucket, with a "Beats %" derived from real data. It
// degrades to a note when there aren't enough submissions to be meaningful.
function RuntimeChart({ runtimes, mine }: { runtimes: number[]; mine: number | null }) {
  if (runtimes.length < 5) {
    return (
      <p className="text-xs text-muted-foreground">
        Not enough accepted submissions yet to chart the runtime distribution.
      </p>
    );
  }
  const BINS = 30;
  const min = runtimes[0];
  const max = runtimes[runtimes.length - 1];
  const binSize = Math.max(1, (max - min) / BINS);
  const counts = new Array(BINS).fill(0);
  for (const r of runtimes) {
    const idx = Math.min(BINS - 1, Math.max(0, Math.floor((r - min) / binSize)));
    counts[idx] += 1;
  }
  const maxCount = Math.max(...counts, 1);
  const mineBin = mine != null ? Math.min(BINS - 1, Math.max(0, Math.floor((mine - min) / binSize))) : -1;
  const beats = mine != null ? (runtimes.filter((r) => r > mine).length / runtimes.length) * 100 : null;

  return (
    <div>
      {beats != null && (
        <p className="mb-2 text-sm">
          <span className="text-muted-foreground">Beats </span>
          <span className="font-semibold text-green-600 dark:text-green-400">{beats.toFixed(1)}%</span>
          <span className="text-muted-foreground"> of accepted submissions</span>
        </p>
      )}
      <div className="flex h-24 items-end gap-px" aria-hidden="true">
        {counts.map((c, i) => (
          <div
            key={i}
            className={cn('flex-1 rounded-t', i === mineBin ? 'bg-primary' : 'bg-muted-foreground/25')}
            style={{ height: `${(c / maxCount) * 100}%` }}
            title={`${Math.round(min + i * binSize)}–${Math.round(min + (i + 1) * binSize)} ms: ${c}`}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>{min} ms</span>
        <span>{max} ms</span>
      </div>
    </div>
  );
}

// SubmissionDetail renders a single submission (verdict, runtime/memory, the
// runtime distribution, and the read-only submitted code) — LeetCode-style,
// opened as a tab in the right panel.
export function SubmissionDetail({ id, slug }: { id: string; slug: string }) {
  const theme = useEditorStore((s) => s.theme);
  const { data: sub, isLoading, isError } = useQuery({
    queryKey: queryKeys.submissions.detail(id),
    queryFn: () => submissionsApi.getById(id),
    staleTime: 60_000,
  });
  const { data: runtimes } = useQuery({
    queryKey: queryKeys.submissions.runtimeDistribution(slug),
    queryFn: () => submissionsApi.getRuntimeDistribution(slug),
    staleTime: 60_000,
    enabled: sub?.verdict === 'AC',
  });

  const accepted = sub?.verdict === 'AC';
  const label = sub ? VERDICT_LABEL[sub.verdict] ?? sub.verdict : '';
  const code = sub?.code ?? '';
  const codeLines = code ? code.split('\n').length : 1;
  const editorHeight = `${Math.min(560, Math.max(140, codeLines * 20 + 24))}px`;
  const memoryMb = sub?.memory_kb != null ? (sub.memory_kb / 1024).toFixed(1) : null;

  return (
    <div className="h-full overflow-y-auto p-4">
      {isLoading && <p className="text-sm text-muted-foreground">Loading submission…</p>}
      {isError && <p className="text-sm text-destructive">Couldn’t load this submission.</p>}
      {sub && (
        <>
          {/* Verdict */}
          <div className="mb-1 flex items-baseline gap-3">
            <h2 className={cn('text-xl font-semibold', verdictColorClass(sub.verdict))}>{label}</h2>
            <span className="text-sm text-muted-foreground">
              {sub.passed_cases}/{sub.total_cases} testcases passed
            </span>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">
            Submitted {new Date(sub.created_at).toLocaleString()}
          </p>

          {/* Runtime + Memory */}
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <p className="text-xs text-muted-foreground">Runtime</p>
              <p className="text-sm font-semibold">
                {sub.runtime_ms != null ? `${sub.runtime_ms} ms` : 'N/A'}
              </p>
            </div>
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <p className="text-xs text-muted-foreground">Memory</p>
              <p className="text-sm font-semibold">{memoryMb != null ? `${memoryMb} MB` : 'N/A'}</p>
            </div>
          </div>

          {/* Runtime distribution (accepted submissions only) */}
          {accepted && (
            <div className="mb-4 rounded-md border border-border bg-muted/20 p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Runtime Distribution</p>
              <RuntimeChart runtimes={runtimes ?? []} mine={sub.runtime_ms ?? null} />
            </div>
          )}

          {!accepted && sub.error_msg && (
            <pre className="mb-4 overflow-x-auto whitespace-pre-wrap rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              {sub.error_msg}
            </pre>
          )}

          {/* Code */}
          <div className="overflow-hidden rounded-md border border-border">
            <div className="border-b border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
              Code | <span className="font-mono capitalize text-foreground">{sub.language}</span>
            </div>
            {code ? (
              <div style={{ height: editorHeight }}>
                <CodeEditor value={code} language={sub.language} theme={theme} readOnly />
              </div>
            ) : (
              <p className="p-3 text-sm text-muted-foreground">Code not available for this submission.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
